import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import type { CreateResidenteRequest, Residente } from '@condo/contracts';
import { CreateResidenteRequestSchema } from '@condo/contracts';
import { isValidRut } from '@condo/domain-chile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RutInput } from '@/components/common/rut-input';
import { useCreateResidente } from './api';
import { Loader2 } from 'lucide-react';

// Narrowed form schema: add RUT validation beyond the shape check and
// normalize empty telefono to undefined so the regex doesn't fail.
const FormSchema = CreateResidenteRequestSchema.extend({
  rut: CreateResidenteRequestSchema.shape.rut.refine(isValidRut, {
    message: 'RUT invalido (dv no coincide).',
  }),
  telefono: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() !== '' ? v : undefined))
    .pipe(
      z
        .string()
        .regex(/^\+56[0-9]{9}$/, 'Formato +569XXXXXXXX')
        .optional(),
    ),
});

interface Props {
  condoId: string;
  onCreated?: (r: Residente) => void;
  onCancel?: () => void;
}

export function ResidenteForm({ condoId, onCreated, onCancel }: Props) {
  const mutation = useCreateResidente(condoId);
  const form = useForm<CreateResidenteRequest>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      rut: '',
      nombre: '',
      apellidos: '',
      email: '',
      telefono: '',
    },
  });

  async function onSubmit(values: CreateResidenteRequest) {
    try {
      const resident = await mutation.mutateAsync(values);
      toast.success('Residente creado', {
        description: `${resident.nombre} ${resident.apellidos}`,
      });
      form.reset();
      onCreated?.(resident);
    } catch (err) {
      toast.error('No se pudo crear el residente');
      console.error(err);
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="nombre">Nombre</Label>
          <Input
            id="nombre"
            className="mt-1.5"
            placeholder="Rodrigo"
            {...form.register('nombre')}
          />
          {form.formState.errors.nombre && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.nombre.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="apellidos">Apellidos</Label>
          <Input
            id="apellidos"
            className="mt-1.5"
            placeholder="Tapia Contreras"
            {...form.register('apellidos')}
          />
          {form.formState.errors.apellidos && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.apellidos.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="rut">RUT</Label>
        <RutInput
          id="rut"
          className="mt-1.5"
          value={form.watch('rut')}
          onChange={(v) => form.setValue('rut', v, { shouldValidate: true })}
        />
        {form.formState.errors.rut && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">
            {form.formState.errors.rut.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Correo</Label>
          <Input
            id="email"
            type="email"
            className="mt-1.5"
            placeholder="residente@condominio.cl"
            {...form.register('email')}
          />
          {form.formState.errors.email && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="telefono">Telefono</Label>
          <Input
            id="telefono"
            className="mt-1.5"
            placeholder="+56912345678"
            {...form.register('telefono')}
          />
          {form.formState.errors.telefono && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.telefono.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-[var(--color-hairline)] pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            'Crear residente'
          )}
        </Button>
      </div>
    </form>
  );
}
